import { Size } from "chakra-react-select";
import { ReactElement } from "react";
import { FieldPath, FieldValues } from "react-hook-form";

import { Box } from "@chakra-ui/react";
import { ControlledInput } from "../ControlledInput/ControlledInput";
import { ControlledSearchSelectProps as ControlledSearchSelectProperties } from "./ControlledSearchSelect";
import { MultiSearchSelect } from "./MultiSearchSelect";

// Inférer le type des options pour éviter 'any'
type InferOptionType<T> = T extends readonly (infer U)[] ? U : never;

// Type pour les valeurs de sélection avec label/value
type SelectOption<T> = {
  label: string;
  value: T;
};

export const ControlledMultiSearchSelect = <T extends FieldValues, TName extends FieldPath<T>>({
  creatableFunction,
  getOptionLabel,
  highlightResults,
  isClearable,
  isSearchable,
  options,
  renderOption: RenderOption,
  setValue,
  singleValue,
  size,
  ...controlledProperties
}: ControlledSearchSelectProperties<T, TName, true>): ReactElement => {
  type OptionType = InferOptionType<typeof options>;

  return (
    <ControlledInput
      {...controlledProperties}
      render={({ onChange, value, ...field }) => {
        const createSelectOption = (v: OptionType): SelectOption<OptionType> => ({
          label: (getOptionLabel ? getOptionLabel(v) : String(v)) ?? "",
          value: v,
        });

        const transformedValue: SelectOption<OptionType>[] =
          value == null || !Array.isArray(value) ? [] : value.map(createSelectOption);

        const handleSelectOption = (data: OptionType | null): SelectOption<OptionType> | null => {
          return data == null ? null : createSelectOption(data);
        };

        return (
          <MultiSearchSelect<OptionType>
            creatableFunction={creatableFunction}
            highlightResults={highlightResults}
            isClearable={isClearable}
            isSearchable={isSearchable}
            onChange={(values) => {
              onChange(values.map((value) => value.value));
            }}
            options={options ?? []}
            renderOption={
              RenderOption
                ? ({ data, selectOption, ...rest }) => (
                    <Box onClick={() => selectOption(handleSelectOption(data.value))}>
                      <RenderOption data={data.value} selectOption={selectOption} {...rest} />
                    </Box>
                  )
                : undefined
            }
            setValue={setValue}
            singleValue={singleValue}
            size={size as Size}
            value={transformedValue}
            {...field}
          />
        );
      }}
      size={size}
    />
  );
};
